import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { type QAFilters, getQARollingDays } from '@/utils/qaFilters';
import {
  buildCampaignComplianceExceptions,
  hasContactComplianceSignal,
  type CampaignComplianceException,
  type ContactComplianceSummary,
} from '@/utils/contactCompliance';
import {
  buildDailyAICostEstimates,
  type DailyAICostEstimate,
} from '@/utils/qaModelCosts';
import {
  buildCampaignScoreBreakdown,
  buildScoreBuckets,
  type CampaignScoreBreakdown,
  type QAScoreBand,
} from '@/utils/qaScores';

interface QAKpis {
  totalCalls: number;
  transferredPercentage: number;
  avgConcernScore: number | null;
  avgUpsetScore: number | null;
  avgDuration: number;
  contactCompliance: ContactComplianceSummary;
}

export interface QAReviewProgress {
  totalCalls: number;
  clearCalls: number;
  transferredTotal: number;
  reviewRequiredTotal: number;
  noReviewRequiredCalls: number;
  notScoredCalls: number;
  pendingReview: number;
  reviewedValid: number;
  reviewedInvalid: number;
  reviewedTotal: number;
  /** % of all review-routed calls that have been human-reviewed */
  reviewCompletionPct: number;
  breakdown: Array<{ name: string; value: number; color: string }>;
}

interface QADashboardSummary {
  kpis: QAKpis;
  reviewProgress: QAReviewProgress;
}

export interface QACall {
  id: string;
  Date: string;
  Publisher: string;
  Campaign: string;
  'Caller ID': string;
  Duration: string;
  Sentiment: string | null;
  'AI Transfer Disposition': string | null;
  'AI Quality Review'?: string | null;
  'AI Concern Score': number | string | null;
  'AI Upset Score': number | string | null;
  concern_score?: number | null;
  upset_score?: number | null;
  concern_evidence?: string | null;
  upset_evidence?: string | null;
  'Human Reviewed': string | null;
  Transcript?: string | null;
  Recording?: string | null;
  Summary?: string | null;
  'AI Transfer Notes'?: string | null;
  'AI Upset Score Notes'?: string | null;
  'AI Concern Bot Notes'?: string | null;
  'AI Supervisor Notes'?: string | null;
  'Human Reviewed Notes'?: string | null;
  'Reviewed By'?: string | null;
  summary_source?: 'legacy' | 'whisper' | 'court_rationale' | null;
  transcript_source?: 'legacy' | 'whisper' | 'transcript_en' | null;
  qa_scored?: boolean;
  has_transcript?: boolean;
  call_id?: string | null;
  transcript_id?: string | null;
  transfer_pattern?: string | null;
  transfer_partner_named?: string | null;
  transfer_confidence?: string | null;
  transfer_evidence?: string | null;
  prosecutor_case?: string | null;
  prosecutor_would_rule?: string | null;
  prosecutor_case_strength?: string | null;
  prosecutor_guard_failed?: boolean | null;
  defender_case?: string | null;
  defender_rebuttal?: string | null;
  defender_would_rule?: string | null;
  defender_concedes?: boolean | null;
  court_tier_reached?: string | null;
  court_rationale?: string | null;
  supervisor_disposition?: string | null;
  sr_escalated?: boolean | null;
  sr_escalation_reason?: string | null;
  sr_disposition?: string | null;
  sr_overturned_court?: boolean | null;
  needs_human_review?: boolean | null;
  mva_criteria?: Record<string, unknown> | null;
  mva_fail_reasons?: string[] | null;
  mva_evidence?: string | null;
  mva_confidence?: string | null;
  mva_escalated?: boolean | null;
  mva_escalation_reason?: string | null;
  mva_jr_qualified?: string | null;
  mva_verdict_changed?: boolean | null;
  mva_qualified?: string | null;
  source_anomaly?: string | null;
  source_anomaly_evidence?: string | null;
  primary_language?: string | null;
  languages_detected?: string[] | null;
  prompt_version?: string | null;
  prosecutor_model?: string | null;
  defender_model?: string | null;
  supervisor_model?: string | null;
  sr_model?: string | null;
  mva_model?: string | null;
  scored_at?: string | null;
  never_solicited?: boolean | null;
  dnc_request?: boolean | null;
  repeat_contact_complaint?: boolean | null;
  repeat_contact_count?: number | null;
  contact_compliance_assessment_status?: string | null;
  contact_compliance_severity?: string | null;
  contact_compliance_confidence?: string | null;
  contact_compliance_evidence?: unknown;
  contact_compliance_action?: string | null;
  contact_compliance_review_status?: string | null;
  contact_compliance_action_status?: string | null;
  contact_scope?: string | null;
  requires_immediate_suppression?: boolean | null;
  contact_compliance_scored_at?: string | null;
  contact_compliance_model?: string | null;
  contact_compliance_prompt_version?: string | null;
  contact_compliance_detection_source?: string | null;
  contact_compliance_assigned_team?: string | null;
  contact_compliance_action_id?: string | number | null;
  transfer_review_required?: boolean | null;
  transfer_review_status?: 'not_required' | 'pending' | 'confirmed' | 'dismissed' | null;
  contact_compliance_review_required?: boolean | null;
  review_required?: boolean | null;
  overall_review_status?: 'not_required' | 'pending' | 'confirmed' | 'dismissed' | null;
}

interface QACallsResponse {
  calls: QACall[];
  total: number;
}

const QA_READ_SOURCE = 'atbs_compliance_review_calls';

const CONTACT_COMPLIANCE_COLUMNS =
  'never_solicited,dnc_request,repeat_contact_complaint,repeat_contact_count,contact_compliance_assessment_status,contact_compliance_severity,contact_compliance_confidence,contact_compliance_evidence,contact_compliance_action,contact_compliance_review_status,contact_compliance_action_status,contact_scope,requires_immediate_suppression,contact_compliance_scored_at,contact_compliance_prompt_version,contact_compliance_detection_source,contact_compliance_assigned_team,contact_compliance_action_id,transfer_review_required,transfer_review_status,contact_compliance_review_required,review_required,overall_review_status';

const QA_CALL_LIST_COLUMNS =
  `id,Date,Publisher,Campaign,"Caller ID",Duration,Sentiment,"AI Transfer Disposition","AI Concern Score","AI Upset Score",concern_score,upset_score,"Human Reviewed",qa_scored,has_transcript,primary_language,needs_human_review,court_tier_reached,sr_escalated,sr_disposition,mva_escalated,prosecutor_model,defender_model,supervisor_model,sr_model,mva_model,contact_compliance_model,${CONTACT_COMPLIANCE_COLUMNS}`;

const QA_CALL_DETAIL_COLUMNS =
  `id,created_at,Date,Publisher,Campaign,"Caller ID",Duration,Sentiment,"AI Transfer Disposition","AI Quality Review","AI Concern Score","AI Upset Score",concern_score,upset_score,concern_evidence,upset_evidence,"Human Reviewed",Transcript,Recording,Summary,"AI Transfer Notes","AI Upset Score Notes","AI Concern Bot Notes","AI Supervisor Notes","Human Reviewed Notes","Reviewed By",summary_source,transcript_source,qa_scored,has_transcript,call_id,transcript_id,transfer_pattern,transfer_partner_named,transfer_confidence,transfer_evidence,prosecutor_case,prosecutor_would_rule,prosecutor_case_strength,prosecutor_guard_failed,defender_case,defender_rebuttal,defender_would_rule,defender_concedes,court_tier_reached,court_rationale,supervisor_disposition,sr_escalated,sr_escalation_reason,sr_disposition,sr_overturned_court,needs_human_review,mva_criteria,mva_fail_reasons,mva_evidence,mva_confidence,mva_escalated,mva_escalation_reason,mva_jr_qualified,mva_verdict_changed,mva_qualified,source_anomaly,source_anomaly_evidence,primary_language,languages_detected,prompt_version,prosecutor_model,defender_model,supervisor_model,sr_model,mva_model,scored_at,contact_compliance_model,${CONTACT_COMPLIANCE_COLUMNS}`;

export function requiresHumanReview(
  call: Pick<
    QACall,
    | 'AI Transfer Disposition'
    | 'needs_human_review'
    | 'never_solicited'
    | 'dnc_request'
    | 'repeat_contact_complaint'
    | 'review_required'
  >
): boolean {
  if (typeof call.review_required === 'boolean') return call.review_required;
  return (
    call.needs_human_review === true ||
    call['AI Transfer Disposition']?.toLowerCase() === 'transferred' ||
    hasContactComplianceSignal(call)
  );
}

function applyContactComplianceFilter(query: any, signal?: QAFilters['consumerSignal']) {
  if (!signal) return query;
  if (signal === 'any') {
    return query.or(
      'never_solicited.eq.true,dnc_request.eq.true,repeat_contact_complaint.eq.true'
    );
  }
  return query.eq(signal, true);
}

function applyScoreFilter(query: any, column: 'concern_score' | 'upset_score', band?: QAScoreBand) {
  if (!band || band === 'all') return query;

  switch (band) {
    case 'high':
      return query.gte(column, 7);
    case 'medium':
      return query.gte(column, 4).lte(column, 6);
    case 'low':
      return query.gte(column, 0).lte(column, 3);
    case 'unscored':
      return query.is(column, null);
    default:
      return query;
  }
}

function applyReviewStatusFilter(query: any, reviewStatus?: QAFilters['reviewStatus']) {
  if (!reviewStatus || reviewStatus === 'all') return query;

  switch (reviewStatus) {
    case 'reviewed':
      return query.or(
        'overall_review_status.eq.confirmed,overall_review_status.eq.dismissed'
      );
    case 'unreviewed':
      return query.eq('overall_review_status', 'pending');
    case 'no_review':
      return query.eq('overall_review_status', 'not_required');
    case 'valid':
      return query.eq('overall_review_status', 'confirmed');
    case 'invalid':
      return query.eq('overall_review_status', 'dismissed');
    default:
      return query;
  }
}

export function parseDurationSeconds(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null;

  const text = String(raw).trim();
  if (!text) return null;

  // hh:mm:ss or mm:ss
  if (text.includes(':')) {
    const parts = text.split(':').map((p) => parseInt(p, 10));
    if (parts.some((p) => Number.isNaN(p))) return null;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return null;
  }

  const asNumber = parseFloat(text.replace(/,/g, ''));
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;

  const digits = parseInt(text.replace(/\D/g, ''), 10);
  return Number.isFinite(digits) && digits > 0 ? digits : null;
}

/** Minimum duration for transfer courtroom scoring; contact compliance remains independent. */
export const TRANSFER_QA_MIN_SECONDS = 10;

/** Format a database date without shifting date-only values across time zones. */
export function formatCallDate(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '—';

  const text = String(raw).trim();
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(text);

  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

/** Format a call duration into a compact label, e.g. "4:05" or "45s". */
export function formatDuration(raw: unknown): string {
  const seconds = parseDurationSeconds(raw);
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * A call is "too short" if it has a known duration below the transfer-QA threshold.
 * These calls remain eligible for independent contact-compliance analysis when
 * intelligible speech or a transcript is available.
 */
export function isCallTooShort(call: { Duration?: unknown } | null | undefined): boolean {
  if (!call) return false;
  const seconds = parseDurationSeconds(call.Duration);
  return seconds !== null && seconds < TRANSFER_QA_MIN_SECONDS;
}

// Build date range filter
export function getDateRangeFilter(filters: QAFilters) {
  const now = new Date();
  let startDate: Date;

  switch (filters.dateRange) {
    case 'last7d':
      startDate = new Date(now.setDate(now.getDate() - 6));
      break;
    case 'last14d':
      startDate = new Date(now.setDate(now.getDate() - 13));
      break;
    case 'last30d':
      startDate = new Date(now.setDate(now.getDate() - 29));
      break;
    case 'last90d':
      startDate = new Date(now.setDate(now.getDate() - 89));
      break;
    case 'custom':
      startDate = filters.startDate ? new Date(filters.startDate) : new Date(0);
      break;
    default:
      startDate = new Date(now.setDate(now.getDate() - 6));
  }

  return startDate.toISOString().split('T')[0];
}

function toDateKey(value?: string | null) {
  if (!value) return null;
  return value.split('T')[0];
}

function isTransferred(disposition?: string | null) {
  return disposition?.toLowerCase() === 'transferred';
}

function pctChange(current: number, prior: number) {
  if (prior === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - prior) / prior) * 1000) / 10;
}

function daysAgo(days: number) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

function countByKey(rows: Array<Record<string, any>>, key: string, limit = 10) {
  const map = new Map<string, number>();
  for (const row of rows) {
    const label = (row[key] as string)?.trim() || 'Unknown';
    map.set(label, (map.get(label) || 0) + 1);
  }
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

async function getQADashboardSummary(filters: QAFilters): Promise<QADashboardSummary> {
  const { data, error } = await supabase.rpc('get_atbs_dashboard_summary', {
    p_start_date: getDateRangeFilter(filters),
    p_end_date:
      filters.dateRange === 'custom' && filters.endDate ? filters.endDate : null,
    p_transfer_only: filters.transferOnly,
    p_concern_score:
      filters.concernScore ?? (filters.concernOnly ? 'high' : null),
    p_upset_score: filters.upsetScore ?? null,
    p_review_status: filters.reviewStatus ?? null,
    p_consumer_signal: filters.consumerSignal ?? null,
    p_sentiment: filters.sentiment ?? null,
    p_campaign: filters.campaign ?? null,
  });

  if (error) throw error;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('ATBS dashboard summary returned an invalid response.');
  }

  const summary = data as unknown as {
    kpis?: QAKpis;
    reviewProgress?: Omit<QAReviewProgress, 'breakdown'>;
  };
  if (!summary.kpis || !summary.reviewProgress) {
    throw new Error('ATBS dashboard summary is incomplete.');
  }

  const progress = summary.reviewProgress;
  return {
    kpis: summary.kpis,
    reviewProgress: {
      ...progress,
      breakdown: [
        { name: 'Pending Review', value: progress.pendingReview, color: '#eab308' },
        { name: 'Valid Issue', value: progress.reviewedValid, color: '#dc2626' },
        { name: 'Invalid Issue', value: progress.reviewedInvalid, color: '#16a34a' },
        {
          name: 'No Review Required',
          value: progress.noReviewRequiredCalls,
          color: '#94a3b8',
        },
        { name: 'Not Scored', value: progress.notScoredCalls, color: '#64748b' },
      ].filter((item) => item.value > 0),
    },
  };
}

function useQADashboardSummary(filters: QAFilters) {
  return useQuery({
    queryKey: ['qa-dashboard-summary', filters],
    queryFn: () => getQADashboardSummary(filters),
    staleTime: 1000 * 60 * 2,
  });
}

// KPI and review-progress consumers share this query key. React Query issues
// one RPC even though the two widgets mount independently.
export function useQAKpis(filters: QAFilters) {
  const query = useQADashboardSummary(filters);
  return { ...query, data: query.data?.kpis };
}

// Fetch QA calls list
export function useQACalls(filters: QAFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: ['qa-calls', filters, page, pageSize],
    queryFn: async (): Promise<QACallsResponse> => {
      const startDate = getDateRangeFilter(filters);

      let query = supabase
        .from(QA_READ_SOURCE)
        .select(QA_CALL_LIST_COLUMNS, { count: 'exact' })
        .gte('Date', startDate)
        .order('Date', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (filters.dateRange === 'custom' && filters.endDate) {
        query = query.lte('Date', filters.endDate);
      }

      // Apply transfer filter (skip when explicitly filtering no-review clear calls)
      if (filters.transferOnly && filters.reviewStatus !== 'no_review') {
        query = query.eq('AI Transfer Disposition', 'Transferred');
      }

      query = applyScoreFilter(
        query,
        'concern_score',
        filters.concernScore ?? (filters.concernOnly ? 'high' : undefined)
      );
      query = applyScoreFilter(query, 'upset_score', filters.upsetScore);

      query = applyReviewStatusFilter(query, filters.reviewStatus);
      query = applyContactComplianceFilter(query, filters.consumerSignal);

      // Apply sentiment filter
      if (filters.sentiment) {
        query = query.ilike('Sentiment', filters.sentiment);
      }

      // Apply campaign filter
      if (filters.campaign) {
        query = query.eq('Campaign', filters.campaign);
      }

      // Apply search
      if (filters.searchText) {
        query = query.or(
          `"Caller ID".ilike.%${filters.searchText}%,Campaign.ilike.%${filters.searchText}%,Publisher.ilike.%${filters.searchText}%,Transcript.ilike.%${filters.searchText}%`
        );
      }

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        calls: data || [],
        total: count || 0,
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export interface QAInsightsData {
  comparisons: {
    thisWeek: number;
    lastWeek: number;
    weekChangePct: number;
    today: number;
    yesterday: number;
    dayChangePct: number;
    thisWeekTransfers: number;
    lastWeekTransfers: number;
    todayTransfers: number;
    yesterdayTransfers: number;
  };
  dailyVolume: Array<{ date: string; total: number; transferred: number; clear: number }>;
  weeklyVolume: Array<{ week: string; total: number; transferred: number; clear: number }>;
  byPublisher: Array<{ name: string; value: number; transfers: number; transferRate: number }>;
  byCampaign: Array<{ name: string; value: number }>;
  bySentiment: Array<{ name: string; value: number }>;
  concernBuckets: Array<{ name: string; value: number }>;
  upsetBuckets: Array<{ name: string; value: number }>;
  campaignScoreBreakdown: CampaignScoreBreakdown[];
  transferRateTrend: Array<{ date: string; rate: number; total: number }>;
  topTransferPublishers: Array<{ name: string; transfers: number; total: number; rate: number }>;
  campaignComplianceExceptions: CampaignComplianceException[];
  dailyAICost: DailyAICostEstimate[];
  aiCostSummary: {
    analyzedCalls: number;
    modelRuns: number;
    estimatedSpendUsd: number;
    averagePerAnalyzedCallUsd: number;
    unpricedRuns: number;
  };
}

const INSIGHTS_COLUMNS =
  'id,Date,Publisher,Campaign,Sentiment,"AI Transfer Disposition","AI Concern Score","AI Upset Score",concern_score,upset_score,Duration,qa_scored,prosecutor_would_rule,defender_would_rule,supervisor_disposition,court_tier_reached,sr_escalated,sr_disposition,mva_qualified,mva_escalated,prosecutor_model,defender_model,supervisor_model,sr_model,mva_model,never_solicited,dnc_request,repeat_contact_complaint,contact_compliance_assessment_status,contact_compliance_scored_at,contact_compliance_model';

export function useQAInsights(dateRange: Exclude<QAFilters['dateRange'], 'custom'> = 'last7d') {
  return useQuery({
    queryKey: ['qa-insights', dateRange],
    queryFn: async (): Promise<QAInsightsData> => {
      const windowDays = getQARollingDays(dateRange);
      // Inclusive rolling window: today + (days - 1) prior days
      const startDate = daysAgo(windowDays - 1);

      const { data, error } = await supabase
        .from(QA_READ_SOURCE)
        .select(INSIGHTS_COLUMNS)
        .gte('Date', startDate)
        .order('Date', { ascending: true })
        .limit(10000);

      if (error) throw error;

      const rows = data || [];
      const today = daysAgo(0);
      const yesterday = daysAgo(1);
      const thisWeekStart = daysAgo(6);
      const lastWeekStart = daysAgo(13);
      const lastWeekEnd = daysAgo(7);

      let thisWeek = 0;
      let lastWeek = 0;
      let todayCount = 0;
      let yesterdayCount = 0;
      let thisWeekTransfers = 0;
      let lastWeekTransfers = 0;
      let todayTransfers = 0;
      let yesterdayTransfers = 0;

      const dailyMap = new Map<string, { total: number; transferred: number; clear: number }>();
      const weeklyMap = new Map<string, { total: number; transferred: number; clear: number }>();
      const publisherMap = new Map<string, { total: number; transfers: number }>();

      for (const row of rows) {
        const day = toDateKey(row.Date);
        if (!day) continue;
        const transferred = isTransferred(row['AI Transfer Disposition']);

        if (day >= thisWeekStart) {
          thisWeek += 1;
          if (transferred) thisWeekTransfers += 1;
        } else if (day >= lastWeekStart && day <= lastWeekEnd) {
          lastWeek += 1;
          if (transferred) lastWeekTransfers += 1;
        }

        if (day === today) {
          todayCount += 1;
          if (transferred) todayTransfers += 1;
        } else if (day === yesterday) {
          yesterdayCount += 1;
          if (transferred) yesterdayTransfers += 1;
        }

        const daily = dailyMap.get(day) || { total: 0, transferred: 0, clear: 0 };
        daily.total += 1;
        if (transferred) daily.transferred += 1;
        else daily.clear += 1;
        dailyMap.set(day, daily);

        // ISO week key: Monday as week start label
        const d = new Date(`${day}T00:00:00`);
        const weekDay = d.getDay();
        const mondayOffset = weekDay === 0 ? -6 : 1 - weekDay;
        const monday = new Date(d);
        monday.setDate(d.getDate() + mondayOffset);
        const weekKey = monday.toISOString().split('T')[0];
        const weekly = weeklyMap.get(weekKey) || { total: 0, transferred: 0, clear: 0 };
        weekly.total += 1;
        if (transferred) weekly.transferred += 1;
        else weekly.clear += 1;
        weeklyMap.set(weekKey, weekly);

        const publisher = (row.Publisher as string)?.trim() || 'Unknown';
        const pub = publisherMap.get(publisher) || { total: 0, transfers: 0 };
        pub.total += 1;
        if (transferred) pub.transfers += 1;
        publisherMap.set(publisher, pub);
      }

      // Fill rolling window days for chart continuity
      const dailyVolume: QAInsightsData['dailyVolume'] = [];
      const rollingDates: string[] = [];
      for (let i = windowDays - 1; i >= 0; i--) {
        const date = daysAgo(i);
        rollingDates.push(date);
        const entry = dailyMap.get(date) || { total: 0, transferred: 0, clear: 0 };
        dailyVolume.push({ date, ...entry });
      }

      const dailyAICost = buildDailyAICostEstimates(rows as QACall[], rollingDates);
      const aiCostSummary = dailyAICost.reduce(
        (summary, day) => {
          summary.analyzedCalls += day.analyzedCalls;
          summary.modelRuns += day.modelRuns;
          summary.estimatedSpendUsd += day.estimatedSpendUsd;
          summary.unpricedRuns += day.unpricedRuns;
          return summary;
        },
        {
          analyzedCalls: 0,
          modelRuns: 0,
          estimatedSpendUsd: 0,
          averagePerAnalyzedCallUsd: 0,
          unpricedRuns: 0,
        }
      );
      aiCostSummary.averagePerAnalyzedCallUsd =
        aiCostSummary.analyzedCalls > 0
          ? aiCostSummary.estimatedSpendUsd / aiCostSummary.analyzedCalls
          : 0;

      const weeklyVolume = Array.from(weeklyMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-8)
        .map(([week, values]) => ({ week, ...values }));

      const byPublisher = Array.from(publisherMap.entries())
        .map(([name, stats]) => ({
          name,
          value: stats.total,
          transfers: stats.transfers,
          transferRate: stats.total > 0 ? Math.round((stats.transfers / stats.total) * 1000) / 10 : 0,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 12);

      const topTransferPublishers = Array.from(publisherMap.entries())
        .map(([name, stats]) => ({
          name,
          transfers: stats.transfers,
          total: stats.total,
          rate: stats.total > 0 ? Math.round((stats.transfers / stats.total) * 1000) / 10 : 0,
        }))
        .filter((p) => p.transfers > 0)
        .sort((a, b) => b.transfers - a.transfers || b.rate - a.rate)
        .slice(0, 10);

      const concernBuckets = buildScoreBuckets(rows as QACall[], 'concern');
      const upsetBuckets = buildScoreBuckets(rows as QACall[], 'upset');
      const campaignScoreBreakdown = buildCampaignScoreBreakdown(rows as QACall[]);

      const transferRateTrend = dailyVolume.map((d) => ({
        date: d.date,
        total: d.total,
        rate: d.total > 0 ? Math.round((d.transferred / d.total) * 1000) / 10 : 0,
      }));
      const campaignComplianceExceptions = buildCampaignComplianceExceptions(
        rows as QACall[]
      );

      return {
        comparisons: {
          thisWeek,
          lastWeek,
          weekChangePct: pctChange(thisWeek, lastWeek),
          today: todayCount,
          yesterday: yesterdayCount,
          dayChangePct: pctChange(todayCount, yesterdayCount),
          thisWeekTransfers,
          lastWeekTransfers,
          todayTransfers,
          yesterdayTransfers,
        },
        dailyVolume,
        weeklyVolume,
        byPublisher,
        byCampaign: countByKey(rows, 'Campaign', 10),
        bySentiment: countByKey(rows, 'Sentiment', 6),
        concernBuckets,
        upsetBuckets,
        campaignScoreBreakdown,
        transferRateTrend,
        topTransferPublishers,
        campaignComplianceExceptions,
        dailyAICost,
        aiCostSummary,
      };
    },
    staleTime: 1000 * 60 * 5,
  });
}

// Fetch single call details
export function useQACall(callId: string | null) {
  return useQuery({
    queryKey: ['qa-call', callId],
    queryFn: async (): Promise<QACall | null> => {
      if (!callId) return null;

      const { data, error } = await supabase
        .from(QA_READ_SOURCE)
        .select(QA_CALL_DETAIL_COLUMNS)
        .eq('id', callId)
        .single();

      if (error) throw error;

      return data;
    },
    enabled: !!callId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Save review
interface ReviewData {
  callId: string;
  reviewKind: 'transfer' | 'contact_compliance';
  isValid: boolean;
  notes: string;
  actionRequired: boolean;
}

export function useSaveReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reviewData: ReviewData) => {
      const callId = Number(reviewData.callId);
      if (!Number.isSafeInteger(callId)) throw new Error('Invalid call ID');

      const { data, error } = await supabase.rpc('save_atbs_call_review', {
        p_call_id: callId,
        p_review_kind: reviewData.reviewKind,
        p_outcome: reviewData.isValid ? 'confirmed' : 'dismissed',
        p_notes: reviewData.notes,
        p_action_required: reviewData.actionRequired,
      });

      if (error) throw error;

      return data;
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['qa-calls'] });
      queryClient.invalidateQueries({ queryKey: ['atbs-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['atbs-transfer-call'] });
      queryClient.invalidateQueries({ queryKey: ['qa-dashboard-summary'] });
      queryClient.invalidateQueries({ queryKey: ['qa-kpis'] });
      queryClient.invalidateQueries({ queryKey: ['qa-call'] });
      queryClient.invalidateQueries({ queryKey: ['qa-insights'] });
      queryClient.invalidateQueries({ queryKey: ['qa-review-progress'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reviews-count'] });
    },
  });
}

/** Review progress for the date range — ignores transfer/status filters so empty queues are explainable */
export function useQAReviewProgress(filters: QAFilters) {
  const query = useQADashboardSummary(filters);
  return { ...query, data: query.data?.reviewProgress };
}

// Live ATBS alert count: transferred calls still awaiting transfer review in
// the same rolling seven-day window used by the default Review queue.
export function usePendingReviewsCount() {
  return useQuery({
    queryKey: ['pending-reviews-count'],
    queryFn: async (): Promise<number> => {
      const rollingSevenDaysStart = getDateRangeFilter({
        dateRange: 'last7d',
        transferOnly: true,
        concernOnly: false,
      });

      const { data, error } = await supabase.rpc('get_atbs_transfer_queue', {
        p_start_date: rollingSevenDaysStart,
        p_bucket: 'pending',
        p_limit: 1,
      });

      if (error) throw error;

      return data?.counts?.pending ?? 0;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}

